/**
 * SocialPulsePlaces — the geographic gateway page for the SocialPulse network.
 *
 * This page implements Phase 2 of the Geographic Network Gateway PRD.
 * SocialPulse.com does NOT host long-form geographic encyclopedias. It
 * *routes*: seven scope cards + a live portal registry + a live world map
 * hand every visitor cleanly to the specialist portal that owns their
 * scope (WorldPulse, USAPulse, Indias, Kerala.com, etc.).
 *
 * Structure:
 *   1. Hero eyebrow + gateway explainer + search CTA
 *   2. Explore by Scope — 7 cards (World, Country, State, County, City,
 *      Village, Neighborhood)
 *   3. Choose Your Portal — <PortalRegistryStrip /> pulling from
 *      /api/portals (the live seed-backed registry)
 *   4. Live Portal Map — the existing LivePortalMap (only plots verified
 *      entities via /api/locations lookups)
 *   5. Instant Router demo — three example hand-offs powered by the
 *      Geographic Routing Engine (/api/route/resolve)
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Compass,
  Globe2,
  Landmark,
  Building2,
  Trees,
  MapPin,
  Map,
  Home,
  Search,
  Sparkles,
} from "lucide-react";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";
import LivePortalMap from "@/components/socialpulse/LivePortalMap";
import PortalRegistryStrip from "@/components/socialpulse/PortalRegistryStrip";
import GeoDestinationCard from "@/components/socialpulse/GeoDestinationCard";

const SCOPES = [
  { key: "world",        title: "World",        icon: Globe2,   tint: "sky",     lead: "The whole network — cross-country stories, climate, diplomacy.", href: "/places?level=world" },
  { key: "country",      title: "Country",      icon: Map,      tint: "blue",    lead: "Start at the country level — pick a nation portal to dive in.",  href: "/places?level=country" },
  { key: "state",        title: "State",        icon: Landmark, tint: "violet",  lead: "US states, Indian states, provinces and regions.",                href: "/places?level=state" },
  { key: "county",       title: "County",       icon: Compass,  tint: "amber",   lead: "County and district portals — where local government lives.",    href: "/places?level=county" },
  { key: "city",         title: "City",         icon: Building2,tint: "emerald", lead: "City halls, neighborhoods, civic pulse and community.",           href: "/places?level=city" },
  { key: "village",      title: "Village",      icon: Trees,    tint: "green",   lead: "Small places with big personalities — the founder's home too.",   href: "/places?level=village" },
  { key: "neighborhood", title: "Neighborhood", icon: Home,     tint: "rose",    lead: "Blocks, wards, and everyday corners of your city.",               href: "/places?level=neighborhood" },
];

const TINTS = {
  sky:     { bg: "bg-sky-50",     text: "text-sky-700",     ring: "ring-sky-200",     bar: "from-sky-200 to-sky-100",     dot: "bg-sky-500" },
  blue:    { bg: "bg-blue-50",    text: "text-blue-700",    ring: "ring-blue-200",    bar: "from-blue-200 to-blue-100",    dot: "bg-blue-500" },
  violet:  { bg: "bg-violet-50",  text: "text-violet-700",  ring: "ring-violet-200",  bar: "from-violet-200 to-violet-100",dot: "bg-violet-500" },
  amber:   { bg: "bg-amber-50",   text: "text-amber-700",   ring: "ring-amber-200",   bar: "from-amber-200 to-amber-100",  dot: "bg-amber-500" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", bar: "from-emerald-200 to-emerald-100", dot: "bg-emerald-500" },
  green:   { bg: "bg-green-50",   text: "text-green-700",   ring: "ring-green-200",   bar: "from-green-200 to-green-100",  dot: "bg-green-500" },
  rose:    { bg: "bg-rose-50",    text: "text-rose-700",    ring: "ring-rose-200",    bar: "from-rose-200 to-rose-100",    dot: "bg-rose-500" },
};

const ROUTER_DEMOS = [
  { label: "Cochin news",      country: "India",         state: "Kerala",     city: "Cochin",   topic: "news"  },
  { label: "San Jose civic",   country: "United States", state: "California", city: "San Jose", topic: "civic" },
  { label: "Blog in India",    country: "India",         topic: "blog" },
];

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function ScopeCard({ scope }) {
  const t = TINTS[scope.tint] || TINTS.violet;
  const Icon = scope.icon;
  return (
    <li>
      <Link
        to={scope.href}
        data-testid={`sp-places-scope-${scope.key}`}
        className="group flex flex-col rounded-2xl ring-1 ring-slate-200 bg-white overflow-hidden hover:ring-violet-300 hover:shadow-md transition-all h-full"
      >
        <div className={`h-1.5 bg-gradient-to-r ${t.bar}`} />
        <div className="p-5 flex-1 flex flex-col gap-3">
          <span className={`w-11 h-11 grid place-items-center rounded-2xl ${t.bg} ${t.text} ring-1 ${t.ring}`}>
            <Icon className="w-5 h-5" strokeWidth={2.2} />
          </span>
          <div>
            <h3 className={`font-serif font-bold text-lg leading-tight ${t.text}`}>{scope.title}</h3>
            <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{scope.lead}</p>
          </div>
          <span className="mt-auto inline-flex items-center gap-1 text-xs font-semibold text-slate-500 group-hover:text-violet-700 pt-2">
            Explore <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </Link>
    </li>
  );
}

function Section({ title, subtitle, id, children, testid }) {
  return (
    <section className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 py-10 sm:py-14" id={id} data-testid={testid}>
      <h2 className="font-serif font-bold text-slate-900 text-xl sm:text-2xl">{title}</h2>
      {subtitle && (
        <p className="mt-1 text-slate-500 text-sm max-w-2xl">{subtitle}</p>
      )}
      <div className="mt-6">{children}</div>
    </section>
  );
}

export default function SocialPulsePlaces() {
  useEffect(() => {
    document.title = "Places — SocialPulse Network Gateway";
    setMeta(
      "description",
      "SocialPulse.com is the network router — pick your scope (world → country → state → village) and land on the portal that owns it."
    );
    setLink("canonical", "https://socialpulse.com/places");
    setMeta("robots", "index, follow");
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="sp-places-page">
      <SocialPulseHeader />

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{
          background: "linear-gradient(140deg, #F0F6FF 0%, #F5F1FA 40%, #ECFDF5 100%)",
        }}
      >
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 pt-12 sm:pt-16 pb-12 sm:pb-16 relative">
          <div
            aria-hidden
            className="absolute -top-16 -left-16 w-72 h-72 rounded-full blur-3xl opacity-45 pointer-events-none"
            style={{ background: "radial-gradient(circle, #A7F3D0 0%, transparent 70%)" }}
          />
          <div
            aria-hidden
            className="absolute top-8 right-0 w-80 h-80 rounded-full blur-3xl opacity-40 pointer-events-none"
            style={{ background: "radial-gradient(circle, #DDD6FE 0%, transparent 75%)" }}
          />

          <div className="relative max-w-3xl">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[.22em] text-violet-700">
              <MapPin className="w-3.5 h-3.5" /> Places · Network Gateway
            </div>
            <h1
              className="mt-3 font-serif font-bold tracking-tight text-slate-900 leading-[1.05] text-3xl sm:text-4xl lg:text-5xl"
              data-testid="sp-places-h1"
            >
              Every place is a{" "}
              <span className="text-emerald-700">pulse.</span>
            </h1>
            <p className="mt-4 sm:mt-5 text-slate-600 text-base sm:text-lg leading-relaxed max-w-2xl">
              SocialPulse.com doesn't try to recreate every geography — it{" "}
              <span className="font-semibold text-slate-800">routes</span> you
              to the specialist portal that owns your scope. Pick a level below
              or search for a place directly.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/search"
                data-testid="sp-places-cta-search"
                className="inline-flex items-center gap-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-5 py-2.5 text-sm transition-colors shadow-sm"
              >
                <Search className="w-4 h-4" /> Search places <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/network"
                data-testid="sp-places-cta-network"
                className="inline-flex items-center gap-2 rounded-full ring-1 ring-slate-300 hover:ring-violet-400 text-slate-700 hover:text-violet-700 font-semibold px-5 py-2.5 text-sm transition-colors bg-white/70 backdrop-blur"
              >
                Browse the network
              </Link>
              <Link
                to="/places?action=request"
                data-testid="sp-places-cta-request"
                className="inline-flex items-center gap-2 rounded-full ring-1 ring-slate-300 hover:ring-emerald-400 text-slate-700 hover:text-emerald-700 font-semibold px-5 py-2.5 text-sm transition-colors bg-white/70 backdrop-blur"
              >
                Request a portal
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Explore by Scope */}
      <Section
        testid="sp-places-scopes"
        title="Explore by scope"
        subtitle="One card per level of geography — from the world all the way down to a single neighborhood."
      >
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SCOPES.map((s) => <ScopeCard key={s.key} scope={s} />)}
        </ul>
      </Section>

      {/* Choose Your Portal (live registry) */}
      <div className="bg-slate-50/60">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 py-10 sm:py-14" data-testid="sp-places-portal-registry">
          <PortalRegistryStrip testid="sp-places-portal-registry-strip" />
        </div>
      </div>

      {/* Live Map */}
      <Section
        testid="sp-places-map"
        title="Live portal map"
        subtitle="Every dot below has a verified location record in the network registry."
      >
        <LivePortalMap />
      </Section>

      {/* Instant router demo */}
      <Section
        testid="sp-places-router"
        title="Try the router"
        subtitle="Type a place + topic and SocialPulse hands you to the portal that owns it."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sp-places-router-demos">
          {ROUTER_DEMOS.map((d, i) => (
            <div key={i} className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-[.15em] text-slate-500 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-violet-500" /> {d.label}
              </p>
              <GeoDestinationCard {...d} testid={`sp-places-router-demo-${i}-geo-destination-card`} />
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-slate-500">
          Powered by{" "}
          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700">
            GET /api/route/resolve
          </code>{" "}
          — deterministic, ownership-matrix backed.
        </p>
      </Section>

      <SocialPulseFooter />
    </div>
  );
}
